import React, { useEffect, useRef, useState } from 'react';
import { playDiscovery, playStamp, playPop } from '../utils/soundEngine';
import useMediaQuery from '../utils/useMediaQuery';

// ─────────────────────────────────────────────────────────────────────────────
// Confetti engine — pure canvas, no library needed
// ─────────────────────────────────────────────────────────────────────────────

const COLORS = [
  '#fbbf24', // amber
  '#34d399', // emerald
  '#60a5fa', // blue
  '#f472b6', // pink
  '#a78bfa', // violet
  '#fb923c', // orange
  '#ffffff',
];

const SHAPES = ['rect', 'circle'];

function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

function createParticle(canvasW, canvasH, originX, originY) {
  const angle  = randomBetween(-Math.PI * 0.9, -Math.PI * 0.1); // upward arc
  const speed  = randomBetween(4, 14);
  return {
    x:     originX ?? randomBetween(canvasW * 0.2, canvasW * 0.8),
    y:     originY ?? canvasH * 0.55,
    vx:    Math.cos(angle) * speed,
    vy:    Math.sin(angle) * speed,
    w:     randomBetween(7, 14),
    h:     randomBetween(4, 9),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
    rot:   randomBetween(0, Math.PI * 2),
    rotV:  randomBetween(-0.18, 0.18),
    alpha: 1,
    drag:  randomBetween(0.97, 0.995),
  };
}

function runConfetti(canvas, count = 90, originX, originY) {
  const ctx = canvas.getContext('2d');
  const W   = canvas.width;
  const H   = canvas.height;
  let   particles = Array.from({ length: count }, () =>
    createParticle(W, H, originX, originY)
  );
  let   rafId;

  function draw() {
    ctx.clearRect(0, 0, W, H);

    particles.forEach(p => {
      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle   = p.color;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);

      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      }
      ctx.restore();

      // Physics
      p.vy   += 0.28;          // gravity
      p.vx   *= p.drag;
      p.vy   *= p.drag;
      p.x    += p.vx;
      p.y    += p.vy;
      p.rot  += p.rotV;
      p.alpha = Math.max(0, p.alpha - 0.012);
    });

    particles = particles.filter(p => p.alpha > 0.01 && p.y < H + 40);

    if (particles.length > 0) {
      rafId = requestAnimationFrame(draw);
    } else {
      ctx.clearRect(0, 0, W, H);
    }
  }

  rafId = requestAnimationFrame(draw);
  return () => {
    cancelAnimationFrame(rafId);
    ctx.clearRect(0, 0, W, H);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Stamp labels per card type
// ─────────────────────────────────────────────────────────────────────────────
const STAMPS = {
  animal:   { emoji: '🐾', text: 'Amazing animal!' },
  food:     { emoji: '🍽️', text: 'Yummy discovery!' },
  landmark: { emoji: '🏛️', text: 'Famous place!' },
  fact:     { emoji: '✨', text: 'Mind blown!' },
  culture:  { emoji: '🎉', text: 'Cool culture!' },
  country:  { emoji: '🌍', text: 'New discovery!' },
};

// ─────────────────────────────────────────────────────────────────────────────
// DiscoveryEffect component
// ─────────────────────────────────────────────────────────────────────────────
/**
 * DiscoveryEffect
 *
 * Props:
 *   trigger   — { type, ts, isFirst } — changes each time a celebration should fire.
 *               isFirst = true  → full confetti + stamp + discovery sound
 *               isFirst = false → stamp only + pop sound
 */
export default function DiscoveryEffect({ trigger }) {
  const canvasRef          = useRef(null);
  const cleanupConfettiRef = useRef(null);
  const [stamp, setStamp]  = useState(null); // { emoji, text } or null
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    if (!trigger) return;

    const { type, isFirst } = trigger;
    const stampData = STAMPS[type] || STAMPS.country;

    // ── Sound ──────────────────────────────────────────────────────
    if (isFirst) {
      playDiscovery();
    } else {
      playPop();
    }

    // ── Stamp animation ────────────────────────────────────────────
    setStamp(stampData);
    const stampTimer = setTimeout(() => setStamp(null), 2200);

    // ── Confetti (first discovery only) ───────────────────────────
    if (isFirst && canvasRef.current && !reducedMotion) {
      // Clean up any previous burst
      if (cleanupConfettiRef.current) cleanupConfettiRef.current();

      // Delay stamp sound slightly so it lands after confetti peaks
      setTimeout(playStamp, 350);

      const canvas = canvasRef.current;
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      cleanupConfettiRef.current = runConfetti(
        canvas,
        110,                        // particle count
        canvas.width  * 0.5,        // origin X — center of screen
        canvas.height * 0.4,        // origin Y — mid-upper area
      );
    }

    return () => {
      clearTimeout(stampTimer);
    };
  // Re-run only when a new effect is triggered (trigger.ts changes); the other
  // values read here are stable for the lifetime of a single trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger?.ts]);

  // Resize canvas if window resizes
  useEffect(() => {
    function resize() {
      if (canvasRef.current) {
        canvasRef.current.width  = canvasRef.current.offsetWidth;
        canvasRef.current.height = canvasRef.current.offsetHeight;
      }
    }
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <>
      {/* Full-screen canvas for confetti — sits above everything, pointer-events:none */}
      <canvas
        ref={canvasRef}
        style={s.canvas}
        aria-hidden="true"
      />

      {/* "New discovery!" stamp */}
      {stamp && (
        <div style={s.stampWrap} key={trigger?.ts} aria-live="assertive">
          <div style={s.stamp}>
            <span style={s.stampEmoji} aria-hidden="true">{stamp.emoji}</span>
            <span style={s.stampText}>{stamp.text}</span>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  canvas: {
    position:      'fixed',
    inset:         0,
    width:         '100%',
    height:        '100%',
    pointerEvents: 'none',
    zIndex:        999,
  },

  stampWrap: {
    position:       'fixed',
    inset:          0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    pointerEvents:  'none',
    zIndex:         1000,
  },

  stamp: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            10,
    background:     'rgba(8, 18, 38, 0.85)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border:         '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius:   22,
    padding:        '22px 36px',
    boxShadow:      '0 24px 60px rgba(2, 6, 23, 0.55), 0 0 0 1px rgba(251, 191, 36, 0.18)',
    fontFamily:     'Inter, system-ui, sans-serif',
    color:          '#fbbf24',
    // Pop in, hold, then fade out
    animation:      'orboot-stamp 2.2s cubic-bezier(0.34,1.56,0.64,1) forwards',
  },

  stampEmoji: {
    fontSize: 44,
    lineHeight: 1,
    display: 'block',
    filter: 'drop-shadow(0 4px 12px rgba(251, 191, 36, 0.35))',
  },

  stampText: {
    fontSize:   18,
    fontWeight: 700,
    color:      '#fbbf24',
    letterSpacing: '-0.01em',
    textAlign:  'center',
  },
};
